import type { ReactNode } from "react";
import { Bookmark, ChevronDown, ListFilter } from "lucide-react";
import "./MobileCandidateDock.css";

export type MobileCandidateDockProps = {
  open: boolean;
  candidateCount: number;
  onToggle: () => void;
  onOpenOffline: () => void;
  children: ReactNode;
  panelId?: string;
  pendingReports?: number;
};

const DEFAULT_PANEL_ID = "mobile-candidate-dock-content";

export function MobileCandidateDock({
  open,
  candidateCount,
  onToggle,
  onOpenOffline,
  children,
  panelId = DEFAULT_PANEL_ID,
  pendingReports = 0,
}: MobileCandidateDockProps) {
  const displayedCandidateCount = Number.isFinite(candidateCount)
    ? Math.max(0, Math.trunc(candidateCount))
    : 0;
  const countDescriptionId = `${panelId}-count`;
  const pendingDescriptionId = `${panelId}-pending`;
  const displayedPendingReports = Number.isFinite(pendingReports)
    ? Math.max(0, Math.trunc(pendingReports))
    : 0;

  return (
    <section
      className={`mobile-candidate-dock${open ? " is-open" : ""}`}
      aria-label="トイレ候補と保存"
    >
      <div className="mobile-candidate-dock__actions">
        <button
          type="button"
          className="mobile-candidate-dock__toggle"
          aria-label={open ? "トイレ候補を閉じる" : "トイレ候補を開く"}
          aria-expanded={open}
          aria-controls={panelId}
          aria-describedby={countDescriptionId}
          onClick={(event) => {
            onToggle();
            if (event.detail === 0) return;
            const button = event.currentTarget;
            if (typeof window.requestAnimationFrame === "function") {
              window.requestAnimationFrame(() => button.blur());
            } else {
              button.blur();
            }
          }}
        >
          <ListFilter aria-hidden="true" />
          <span className="mobile-candidate-dock__toggle-copy">
            <b>候補一覧</b>
            <small id={countDescriptionId}>
              半径2km以内 <strong>{displayedCandidateCount}件</strong>
            </small>
          </span>
          <ChevronDown
            className="mobile-candidate-dock__chevron"
            aria-hidden="true"
          />
        </button>

        <button
          type="button"
          className="mobile-candidate-dock__offline"
          aria-describedby={
            displayedPendingReports > 0 ? pendingDescriptionId : undefined
          }
          onClick={onOpenOffline}
        >
          <Bookmark aria-hidden="true" />
          <span>
            保存・<wbr />オフライン
          </span>
          {displayedPendingReports > 0 ? (
            <i aria-hidden="true">{displayedPendingReports}</i>
          ) : null}
        </button>
        {displayedPendingReports > 0 ? (
          <span className="sr-only" id={pendingDescriptionId}>
            送信待ちの報告 {displayedPendingReports}件
          </span>
        ) : null}
      </div>

      <div
        id={panelId}
        className="mobile-candidate-dock__content"
        role="region"
        aria-label="トイレ候補一覧"
        hidden={!open}
      >
        {children}
      </div>
    </section>
  );
}
